import { LIVE_AGENT_CONNECTING_MESSAGE, LIVE_AGENT_ERROR_MESSAGE } from '../../shared/constants';
import { MessageType, createMessage } from '../../shared/message';

/**
 * Handles the Planner-bot response once the query settles: saves the bot message (or
 * an error message), fires the image-upload analytics, and — when the bot asks to
 * escalate — kicks off the live-agent connect sequence.
 *
 * Pure orchestration extracted from AssistantOrchestrator's botLoading effect. The
 * request itself is issued by `QueryLayerBlock` (Seq F.d), which calls the root's
 * `onSettled` port; the root builds `ctx` from that and its own refs.
 *
 * @param {object} ctx
 * @param {object} ctx.botResponse   raw planner-assistant data (or undefined)
 * @param {any}    ctx.botError      planner-assistant error (or undefined)
 * @param {object} ctx.lastUserMessage
 * @param {{current: any}} ctx.sessionIDRef
 * @param {{current: any}} ctx.actionIDRef
 * @param {(msg: object) => Promise<any>} ctx.saveMessage
 * @param {(v: boolean) => void} ctx.setIsSubmitting
 * @param {() => void} ctx.broadCastMessageUpdate
 * @param {(args: object) => Promise<any>} ctx.initiateAgentChat
 * @param {() => Promise<any>} ctx.updateConnectingToConnected
 * @param {(sessionID: any, actionID: any, outcome: string, question: any) => void} ctx.sendImageUploadAnalytics
 * @param {() => void} ctx.clearActionId  resets the provider's actionID ref
 */
export const handleBotResponse = ({
  botResponse,
  botError,
  lastUserMessage,
  sessionIDRef,
  actionIDRef,
  saveMessage,
  setIsSubmitting,
  broadCastMessageUpdate,
  initiateAgentChat,
  updateConnectingToConnected,
  sendImageUploadAnalytics,
  clearActionId,
}) => {
  const responseData = botResponse?.retrieveDetailsPlannerAssistant || {};

  sendImageUploadAnalytics(
    sessionIDRef.current,
    actionIDRef.current,
    botError ? 'failure' : 'success',
    lastUserMessage?.content
  );

  saveMessage(createMessage(MessageType.BOT, botError ? null : { ...responseData }, {
    userMessageOrigin: lastUserMessage?.messageOrigin || '',
    isImageAttached: lastUserMessage?.isImageAttached || false,
    imageOrigin: lastUserMessage?.imageOrigin || '',
    error: botError
  }))
    .catch(() => {})
    .finally(() => {
      setIsSubmitting(false);
      broadCastMessageUpdate();
    });

  if (!botError && responseData.escalate) {
    // ⚠ The success step MUST be chained before the catch, not after it. `.catch()` returns
    // a RESOLVED promise, so a trailing `.then()` also runs on the failure path — which
    // meant a failed escalation still promoted "connecting to an agent…" to "connected to
    // an associate", telling the user a human was on the line when none was.
    saveMessage(LIVE_AGENT_CONNECTING_MESSAGE)
      .then(() => initiateAgentChat({ text: lastUserMessage?.content || '', actionId: actionIDRef.current }))
      .then(() => updateConnectingToConnected())
      .catch(() => saveMessage(LIVE_AGENT_ERROR_MESSAGE).catch(() => {}))
      .finally(() => {
        clearActionId();
        broadCastMessageUpdate();
        return Promise.resolve();
      });
  } else {
    clearActionId();
  }
};
